// Copyright (c) 2025 UXtechLab.
// Originally created by @taekie. Released under the MIT License. See LICENSE for details.
/**
 * Nesting For Voronoi Utility
 *
 * Transforms flat data into a nested hierarchical structure suitable
 * for d3.hierarchy() and voronoi treemap visualization.
 *
 * Creates a 3-level hierarchy: root -> group -> subgroup -> item
 * Each leaf node contains size values for sizing and references to original data.
 */

import d3 from './utils/d3-bundle.js';

/**
 * Convert flat data array into nested hierarchy structure for voronoi treemap
 *
 * @param {Object[]} data - Array of data objects with group, subgroup, item, and size fields
 * @param {string} [key1='subgroup'] - Field name for first level grouping (subgroup)
 * @param {string} [key2='item'] - Field name for second level grouping (item)
 * @returns {Object} Nested hierarchy object with key/values structure for d3.hierarchy
 *
 * @example
 * const data = [
 *   { group: 'A', subgroup: 'Group1', item: 'Item1', size: 10 },
 *   { group: 'A', subgroup: 'Group1', item: 'Item2', size: 20 }
 * ];
 * const nested = nestingForVoronoi(data);
 * const hierarchy = d3.hierarchy(nested, d => d.values).sum(d => d.size);
 */
export function nestingForVoronoi(
  data,
  key1 = "subgroup",
  key2 = "item"
) {
  // 1. Extract only necessary fields
  const simpleData = data.map((d) => ({
    [key1]: d[key1],
    [key2]: d[key2],
    group: d.group,
    size: d.size ?? 1
  }));

  // 2. 3-level grouping with d3.rollups: group -> key1 -> key2
  const nested = d3.rollups(
    simpleData,
    (d) => d3.sum(d.map((v) => v.size)),
    (d) => d.group,
    (d) => d[key1],
    (d) => d[key2]
  );

  // 3. Helper to convert to dictionary format
  const makeDictionary = (bc, bcData, group) => {
    return bcData.map((k) => {
      const grouping = {
        [key1]: bc,
        [key2]: k[0],
        size: k[1] ? k[1] : 1
      };

      const originalData = data.filter(
        (c) =>
          c.group === group &&
          c[key1] === grouping[key1] &&
          c[key2] === grouping[key2]
      );

      // Keep all original fields on the leaf item (grouping keys + size win),
      // so popups can reference any data field via {field} with no extra wiring.
      const item = { ...(originalData[0] || {}), ...grouping };

      return {
        key: k[0],
        values: [item],
        data: originalData[0],
        raw: originalData
      };
    });
  };

  // 4. Generate final hierarchical structure
  const kv = nested.map(([group, groupData]) => ({
    key: group,
    values: groupData.map(([bc, bcData]) => ({
      key: bc,
      values: makeDictionary(bc, bcData, group)
    }))
  }));

  return {
    key: "root_nest",
    values: kv.filter((d) => d.key)
  };
}

export default nestingForVoronoi;
